import { scrollIntoView } from '@theme/scrolling';

class BoostSliderGallery extends HTMLElement {
  constructor() {
    super();
    this.handleScroll = this.handleScroll.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-truck-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-truck-gallery-slide]')];
    this.previousButton = this.querySelector('[data-truck-gallery-previous]');
    this.nextButton = this.querySelector('[data-truck-gallery-next]');
    this.controls = this.querySelector('[data-truck-gallery-controls]');
    this.status = this.querySelector('[data-truck-gallery-status]');

    if (!this.track || this.slides.length < 2 || !this.previousButton || !this.nextButton) return;

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.previousButton.addEventListener('click', this.handlePrevious);
    this.nextButton.addEventListener('click', this.handleNext);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);
    this.controls?.removeAttribute('hidden');
    this.updateControls();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.updateFrame);
    clearTimeout(this.announcementTimer);
  }

  handleScroll() {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.updateControls());
  }

  handlePrevious() {
    this.goToSlide(this.getCurrentIndex() - 1, true);
  }

  handleNext() {
    this.goToSlide(this.getCurrentIndex() + 1, true);
  }

  handleBlockSelect(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const slide = target.closest('[data-truck-gallery-slide]');
    const index = this.slides.indexOf(slide);
    if (index < 0) return;

    this.goToSlide(index, false);
  }

  getCurrentIndex() {
    const trackRect = this.track.getBoundingClientRect();
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const trackEdge = isRtl ? trackRect.right : trackRect.left;

    if (this.isAtStart(trackRect, isRtl)) return 0;
    if (this.isAtEnd(trackRect)) return this.slides.length - 1;

    return this.slides.reduce(
      (closest, slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const slideEdge = isRtl ? slideRect.right : slideRect.left;
        const distance = Math.abs(slideEdge - trackEdge);
        return distance < closest.distance ? { index, distance } : closest;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY }
    ).index;
  }

  isAtStart(
    trackRect = this.track.getBoundingClientRect(),
    isRtl = getComputedStyle(this.track).direction === 'rtl'
  ) {
    const firstRect = this.slides[0]?.getBoundingClientRect();
    if (!firstRect) return true;

    const styles = getComputedStyle(this.track);
    const startPadding = parseFloat(isRtl ? styles.paddingRight : styles.paddingLeft) || 0;
    const expectedEdge = isRtl ? trackRect.right - startPadding : trackRect.left + startPadding;
    const firstEdge = isRtl ? firstRect.right : firstRect.left;

    return Math.abs(firstEdge - expectedEdge) <= 2;
  }

  isAtEnd(trackRect = this.track.getBoundingClientRect()) {
    const lastRect = this.slides[this.slides.length - 1]?.getBoundingClientRect();
    if (!lastRect) return true;

    return lastRect.left >= trackRect.left - 2 && lastRect.right <= trackRect.right + 2;
  }

  goToSlide(index, announce) {
    const boundedIndex = Math.min(this.slides.length - 1, Math.max(0, index));
    const slide = this.slides[boundedIndex];
    if (!slide) return;

    scrollIntoView(slide, {
      ancestor: this.track,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start',
    });

    if (!announce || !this.status) return;

    clearTimeout(this.announcementTimer);
    this.announcementTimer = setTimeout(
      () => {
        this.status.textContent = `Showing gallery image ${boundedIndex + 1} of ${this.slides.length}.`;
      },
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 350
    );
  }

  updateControls() {
    const trackRect = this.track.getBoundingClientRect();
    const slideRects = this.slides.map((slide) => slide.getBoundingClientRect());
    const contentStart = Math.min(...slideRects.map((rect) => rect.left));
    const contentEnd = Math.max(...slideRects.map((rect) => rect.right));
    const hasOverflow = contentEnd - contentStart > trackRect.width + 2;

    this.previousButton.disabled = !hasOverflow || this.isAtStart(trackRect);
    this.nextButton.disabled = !hasOverflow || this.isAtEnd(trackRect);
    this.controls?.toggleAttribute('hidden', !hasOverflow);
  }
}

if (!customElements.get('boost-slider-gallery')) {
  customElements.define('boost-slider-gallery', BoostSliderGallery);
}
